import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { LOCALE, MAIN_PAGE } from "@constants";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@helpers/translate";
import {
  toggleLoginModal,
  toggleSignUpModal,
} from "@actions/authModalsActions";
import useBodyScrollLock from "@helpers/useBodyScrollLock";
import LandingButton from "@components/landing/buttons/LandingButton";
import BinifyLogo from "@components/library/Icons/BinifyLogo";
import globeIcon from "@assets/img/globe-icon.svg";

import { LANGUAGES, getLanguage } from "@constants/languages";

import burgerIcon from "@assets/images/icons/burger-landing.svg";
import closeIcon from "@assets/images/icons/close-landing.svg";
import "./HeaderLanding.sass";

const LANDING_SECTION_IDS = ["company", "about-us", "benefit", "contact"];

const COMPANY_NAV_ITEMS = [
  {
    messageKey: "common.company",
    variant: "label",
    sectionId: "company",
  },
  {
    messageKey: "common.about_us",
    sectionId: "about-us",
    href: "#about-us",
  },
  {
    messageKey: "common.benefit",
    sectionId: "benefit",
    href: "#benefit",
  },
  {
    messageKey: "common.contact_us",
    sectionId: "contact",
    href: "#contact",
  },
];

const LANDING_NAV_SCROLL_OFFSET = 118;

// Тривалість анімації у мілісекундах.
const LANDING_SCROLL_DURATION = 900;

const getScrollY = () =>
  window.scrollY ||
  window.pageYOffset ||
  document.documentElement.scrollTop ||
  document.body.scrollTop ||
  0;

const easeInOutCubic = (progress) => {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
};

const LanguageSelector = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state) => state.localization);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleLanguageChange = (langCode) => {
    dispatch({
      type: LOCALE.CHANGE_LOCALE,
      payload: langCode,
    });

    setIsDropdownOpen(false);
  };

  const currentLang = getLanguage(locale);

  return (
    <div className="lang-selector" ref={dropdownRef}>
      <button
        type="button"
        className="lang-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <span className="lang-button__text">
          {currentLang.shortLabel}
        </span>

        <img
          src={globeIcon}
          className="lang-button__icon"
          alt="Language"
        />
      </button>

      {isDropdownOpen && (
        <div className="lang-dropdown">
          {LANGUAGES.map((lang) => {
            const Flag = lang.Flag;

            return (
              <button
                type="button"
                key={lang.code}
                className={`lang-dropdown__item ${
                  locale === lang.code
                    ? "lang-dropdown__item--active"
                    : ""
                }`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <Flag
                  className="language-dropdown__flag"
                  style={{ borderRadius: "2.5px" }}
                />

                <span>{lang.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const HeaderLanding = () => {
  const isDesktop = useMediaQuery("(min-width:1150px)");
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { __ } = useTranslation();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState("company");

  const scrollAnimationFrameRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);

  const isLandingHome = location.pathname === MAIN_PAGE;
  const currentPath = location.pathname;

  const isAuthRoute = useMemo(
    () => currentPath.startsWith("/auth/"),
    [currentPath],
  );

  const handleLoginClick = () => {
    if (isAuthRoute) {
      return;
    }

    dispatch(toggleLoginModal(true));
  };

  const handleSignupClick = () => {
    if (isAuthRoute) {
      return;
    }

    dispatch(toggleSignUpModal(true));
  };

  const scrollToPosition = useCallback((targetY) => {
    if (scrollAnimationFrameRef.current) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }

    const startY = getScrollY();
    const distance = targetY - startY;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (prefersReducedMotion || Math.abs(distance) < 1) {
      window.scrollTo(0, targetY);
      isProgrammaticScrollRef.current = false;
      return;
    }

    const startTime = performance.now();

    isProgrammaticScrollRef.current = true;

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;

      const progress = Math.min(
        elapsed / LANDING_SCROLL_DURATION,
        1,
      );

      const easedProgress = easeInOutCubic(progress);
      const nextY = startY + distance * easedProgress;

      window.scrollTo(0, nextY);

      if (progress < 1) {
        scrollAnimationFrameRef.current =
          window.requestAnimationFrame(animateScroll);

        return;
      }

      window.scrollTo(0, targetY);

      scrollAnimationFrameRef.current = null;
      isProgrammaticScrollRef.current = false;
    };

    scrollAnimationFrameRef.current =
      window.requestAnimationFrame(animateScroll);
  }, []);

  const scrollToSection = useCallback(
    (sectionId) => {
      if (sectionId === "company") {
        scrollToPosition(0);
        return;
      }

      const element = document.getElementById(sectionId);

      if (!element) {
        return;
      }

      const elementTop =
        element.getBoundingClientRect().top + getScrollY();

      const targetY =
        elementTop - LANDING_NAV_SCROLL_OFFSET;

      const maxScrollY = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );

      const normalizedTargetY = Math.min(
        Math.max(0, targetY),
        maxScrollY,
      );

      scrollToPosition(normalizedTargetY);
    },
    [scrollToPosition],
  );

  /** У SPA hash-навігація часто не скролить до #id — робимо це явно */
  const handleSectionLinkClick = useCallback(
    (event, sectionId) => {
      if (!LANDING_SECTION_IDS.includes(sectionId)) {
        return;
      }

      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (event.button !== 0) {
        return;
      }

      if (!isLandingHome) {
        return;
      }

      event.preventDefault();

      setActiveSectionId(sectionId);

      navigate(
        {
          pathname: MAIN_PAGE,
          search: location.search,
          hash: sectionId === "company" ? undefined : sectionId,
        },
        {
          replace: true,
          preventScrollReset: true,
        },
      );

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollToSection(sectionId);
        });
      });
    },
    [
      isLandingHome,
      navigate,
      location.search,
      scrollToSection,
    ],
  );

  useBodyScrollLock(isMobileMenuOpen);

  useEffect(() => {
    if (!isLandingHome) {
      setActiveSectionId(null);
      return;
    }

    const raw = (location.hash || "").replace(/^#/, "");

    if (LANDING_SECTION_IDS.includes(raw)) {
      setActiveSectionId(raw);
    } else {
      setActiveSectionId("company");
    }
  }, [isLandingHome, location.hash]);

  useEffect(() => {
    if (
      !isLandingHome ||
      activeSectionId == null ||
      isProgrammaticScrollRef.current
    ) {
      return;
    }

    const raw = (location.hash || "").replace(/^#/, "");
    const fromUrl = LANDING_SECTION_IDS.includes(raw)
      ? raw
      : "company";

    if (fromUrl === activeSectionId) {
      return;
    }

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash:
          activeSectionId === "company"
            ? undefined
            : activeSectionId,
      },
      {
        replace: true,
        preventScrollReset: true,
      },
    );
  }, [
    activeSectionId,
    isLandingHome,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  useEffect(() => {
    if (!isLandingHome) {
      return undefined;
    }

    let rafId = 0;

    const pickActiveFromScroll = () => {
      rafId = 0;

      if (isProgrammaticScrollRef.current) {
        return;
      }

      const y = getScrollY();
      const vh = window.innerHeight || 1;
      const marker = y + LANDING_NAV_SCROLL_OFFSET;

      let active = LANDING_SECTION_IDS[0];

      for (const id of LANDING_SECTION_IDS) {
        const element = document.getElementById(id);

        if (!element) {
          continue;
        }

        const top =
          element.getBoundingClientRect().top + y;

        if (top <= marker + 1) {
          active = id;
        }
      }

      // CTA коротка й близько до низу сторінки:
      // активуємо її, коли вона вже видима.
      const lastId =
        LANDING_SECTION_IDS[
          LANDING_SECTION_IDS.length - 1
        ];

      const lastElement =
        document.getElementById(lastId);

      if (lastElement) {
        const rect =
          lastElement.getBoundingClientRect();

        const isInViewport =
          rect.top < vh && rect.bottom > 0;

        const scrollHeight =
          document.documentElement.scrollHeight || 0;

        const maxY = Math.max(
          0,
          scrollHeight - vh,
        );

        const isNearPageEnd =
          y >= maxY - 24;

        if (
          isInViewport &&
          (
            isNearPageEnd ||
            rect.top < vh * 0.92
          )
        ) {
          active = lastId;
        }
      }

      setActiveSectionId((previousSectionId) =>
        previousSectionId === active
          ? previousSectionId
          : active,
      );
    };

    const onScrollOrResize = () => {
      if (!rafId) {
        rafId =
          window.requestAnimationFrame(
            pickActiveFromScroll,
          );
      }
    };

    pickActiveFromScroll();

    window.addEventListener(
      "scroll",
      onScrollOrResize,
      { passive: true },
    );

    window.addEventListener(
      "resize",
      onScrollOrResize,
    );

    return () => {
      window.removeEventListener(
        "scroll",
        onScrollOrResize,
      );

      window.removeEventListener(
        "resize",
        onScrollOrResize,
      );

      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isLandingHome]);

  useEffect(() => {
    return () => {
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(
          scrollAnimationFrameRef.current,
        );

        scrollAnimationFrameRef.current = null;
      }

      isProgrammaticScrollRef.current = false;
    };
  }, []);

  const handleLogoClick = (event) => {
    if (!isLandingHome) {
      return;
    }

    event.preventDefault();

    setActiveSectionId("company");

    navigate(
      {
        pathname: MAIN_PAGE,
        search: location.search,
        hash: undefined,
      },
      {
        replace: true,
        preventScrollReset: true,
      },
    );

    scrollToPosition(0);
  };

  return (
    <header className="landing-header margin-container">
      <div className="landing-header__container">
        <Link
          className="landing-header__logo"
          to="/"
          onClick={handleLogoClick}
        >
          <BinifyLogo height="24" />
        </Link>

        <nav
          className="landing-header__company-nav"
          aria-label={__("common.company")}
        >
          <div className="landing-header__company-nav-links">
            {COMPANY_NAV_ITEMS.map((item) => {
              const base =
                "landing-header__company-nav-link";

              if (item.variant === "label") {
                const isActive =
                  isLandingHome &&
                  activeSectionId === item.sectionId;

                return (
                  <a
                    key={item.messageKey}
                    href={MAIN_PAGE}
                    className={`${base} ${base}--label${
                      isActive
                        ? ` ${base}--active`
                        : ""
                    }`}
                    aria-current={
                      isActive ? "true" : undefined
                    }
                    onClick={(event) =>
                      handleSectionLinkClick(
                        event,
                        item.sectionId,
                      )
                    }
                  >
                    <span className="text-shark">
                      {__(item.messageKey)}
                    </span>
                  </a>
                );
              }

              const isActive =
                isLandingHome &&
                activeSectionId === item.sectionId;

              return (
                <a
                  key={item.messageKey}
                  href={`${MAIN_PAGE}${item.href}`}
                  className={`${base}${
                    isActive
                      ? ` ${base}--active`
                      : ""
                  }`}
                  onClick={(event) =>
                    handleSectionLinkClick(
                      event,
                      item.sectionId,
                    )
                  }
                >
                  <span className="text-shark">
                    {__(item.messageKey)}
                  </span>
                </a>
              );
            })}
          </div>
        </nav>

        <div className="landing-header__actions">
          <LanguageSelector />

          {!isAuthRoute ? (
            <>
              <LandingButton
                kind="login"
                variant="gray"
                size="small"
                onClick={handleLoginClick}
              >
                {__("login.button.title")}
              </LandingButton>

              <LandingButton
                kind="signup"
                variant="primary"
                size="small"
                onClick={handleSignupClick}
              >
                {__("auth.sign_up")}
              </LandingButton>
            </>
          ) : (
            <>
              <Link
                to="/auth/login"
                style={{ textDecoration: "none" }}
              >
                <LandingButton
                  kind="login"
                  variant="gray"
                  size="small"
                >
                  {__("login.button.title")}
                </LandingButton>
              </Link>

              <Link
                to="/auth/signup"
                style={{ textDecoration: "none" }}
              >
                <LandingButton
                  kind="signup"
                  variant="primary"
                  size="small"
                >
                  {__("auth.sign_up")}
                </LandingButton>
              </Link>
            </>
          )}
        </div>

        {!isDesktop && (
          <div className="landing-header__mobile-actions">
            <LanguageSelector />

            <button
              type="button"
              onClick={() =>
                setIsMobileMenuOpen(
                  !isMobileMenuOpen,
                )
              }
              className="menu-toggle"
            >
              {isMobileMenuOpen ? (
                <img
                  src={closeIcon}
                  className="menu-toggle__icon"
                  alt="Close"
                />
              ) : (
                <img
                  src={burgerIcon}
                  className="menu-toggle__icon"
                  alt="Burger"
                />
              )}
            </button>
          </div>
        )}

        <div
          className={`mobile-menu ${
            isMobileMenuOpen
              ? "mobile-menu--open"
              : ""
          }`}
        >
          <div className="mobile-menu__content">
            <nav
              className="mobile-menu__nav"
              aria-label={__("common.company")}
            >
              {COMPANY_NAV_ITEMS.map((item) => {
                const isTitle =
                  item.variant === "label";

                const isActive =
                  isLandingHome &&
                  activeSectionId === item.sectionId;

                return (
                  <a
                    key={item.messageKey}
                    href={
                      isTitle
                        ? MAIN_PAGE
                        : `${MAIN_PAGE}${item.href}`
                    }
                    className={
                      isTitle
                        ? `mobile-menu__title${
                            isActive
                              ? " mobile-menu__title--active"
                              : ""
                          }`
                        : `mobile-menu__nav-link${
                            isActive
                              ? " mobile-menu__nav-link--active"
                              : ""
                          }`
                    }
                    onClick={(event) => {
                      handleSectionLinkClick(
                        event,
                        item.sectionId,
                      );

                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {isTitle ? (
                      __(item.messageKey)
                    ) : (
                      <span className="text-shark">
                        {__(item.messageKey)}
                      </span>
                    )}
                  </a>
                );
              })}
            </nav>
          </div>

          <div className="mobile-menu__buttons">
            <LandingButton
              kind="signup"
              variant="primary"
              size="small"
              className="mobile-menu__button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                handleSignupClick();
              }}
            >
              {__("auth.sign_up")}
            </LandingButton>

            <LandingButton
              kind="login"
              variant="outline"
              size="small"
              className="mobile-menu__button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                handleLoginClick();
              }}
            >
              {__("login.button.title")}
            </LandingButton>
          </div>
        </div>
      </div>
    </header>
  );
};
